import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { DocumentLibraryService } from '@dealstack/frontend/shared/dataExchange/services/document-library/document-library.service';
import { DocumentLibraryFilter } from '@dealstack/frontend/shared/dataExchange/services/shared/types';
import { NO_MIP_FILTER_VALUE } from '@dealstack/shared/lib/documents/types';
import { mapDocumentsToRows } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/DocumentsCard/investorDocuments.utils.ts';
import { InvestorDocumentRow } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/DocumentsCard/investorDocuments.types.ts';

const DOCUMENTS_PAGE_SIZE = 100;

interface Props {
	dealId: string;
	mipId: string;
	personIds: string[];
}

interface Result {
	rows: InvestorDocumentRow[];
	isLoading: boolean;
	isError: boolean;
}

export const useInvestorDocuments = ({ dealId, mipId, personIds }: Props): Result => {
	const endpoint = formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_LIBRARY_DOCUMENTS_GET, {
		dealId,
	});

	const filters: DocumentLibraryFilter = useMemo(
		() => ({
			pagination: { take: DOCUMENTS_PAGE_SIZE, skip: 0 },
			fieldFilter: { relatedToIds: personIds, mipIds: [mipId, NO_MIP_FILTER_VALUE] },
		}),
		[personIds, mipId]
	);

	const documentsQuery = useQuery({
		queryKey: [endpoint, filters],
		queryFn: ({ signal }) =>
			DocumentLibraryService.getWithFilteringAndOrder(endpoint, filters, signal),
		enabled: personIds.length > 0,
	});

	const documents = documentsQuery.data?.payload?.documents;

	const rows = useMemo(() => mapDocumentsToRows(documents), [documents]);

	return {
		rows,
		isLoading: documentsQuery.isLoading,
		isError: documentsQuery.isError,
	};
};
